/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { deleteLoggedEffort } from '../../../Api/request/timeSheetReport';
import { useAuth } from '../../../Hooks/useAuth';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { TIME_SHEET_REPORT } from '../../../Api/resources/endPoint';
import { MODULE_NAMES } from '../../../Constants/modules';
import { toast } from 'react-toastify';
import { ReactComponent as CalendarIcon } from '../../../Assets/images/svg/calendar_view.svg';
import { ReactComponent as ListIcon } from '../../../Assets/images/svg/list_view.svg';
import AddEffortModal from './AddEffortModal';
import EditEffortModal from './EditEffortModal';
import ActionConfirmModal from './ActionConfirmModal';
import './timeSheetReport.scss';
import dayjs from 'dayjs';

const PAGE_SIZE = 10;
const INITIAL_PAGINATION = { page: 1, limit: PAGE_SIZE, total: 0 };

const TIME_SHEET_REPORT_AVAILABLE_FILTERS = {
  list_view: ['dates', 'projectName'],
  calendar_view: ['dates', 'projectName'],
};


// Pure function to build service URL
const buildServiceURL = (service, path) => {
  const url = appendServiceNameToURL(service);
  if (process.env.NODE_ENV === 'production') {
    return `${process.env.REACT_APP_API_URL}${url}${path}`;
  }
  const port = appendServicePortToURL(service);
  const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return `${baseUrl}:${port}${url}${path}`;
};

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;
  rootNode.querySelectorAll('.button-group button').forEach((btn) => {
    const isReset = Boolean(btn.querySelector('.pi-refresh'));
    const isFilter = btn.classList.contains('table-utility-filter-btn');
    const isNew = btn.classList.contains('table-utility-add-new-btn');
    const isViewToggle = btn.classList.contains('tsr-view-toggle-btn');
    const isExport = !isFilter && !isReset && !isNew && !isViewToggle && Boolean(btn.querySelector('svg'));
    btn.classList.toggle('time-sheet-report-reset-btn', isReset);
    btn.classList.toggle('time-sheet-report-export-btn', isExport);
    btn.classList.toggle('time-sheet-report-add-btn', isNew);
  });
};

const applyTotalRowClass = (container) => {
  if (!container) return;
  const rows = container.querySelectorAll('.p-datatable-tbody tr');
  rows.forEach((row) => {
    const hasTotalText = [...row.cells].some(cell => cell.innerText?.trim() === 'Total');
    if (hasTotalText) {
      row.classList.add('time-sheet-total-row');
    }
  });
};

const getValueByPath = (obj, path) =>
  path.split('.').reduce((acc, key) => acc?.[key], obj);

const ID_CANDIDATES = [
  'timeSheetId', 'timesheetId', 'time_sheet_id',
  'timesheet_id', 'id', 'data_id',
];

const resolveTimeSheetId = (rowData) => {
  for (const key of ID_CANDIDATES) {
    const value = key.includes('.') ? getValueByPath(rowData, key) : rowData?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value);
    }
  }
  return '';
};

const buildExportFileName = (fileName) => {
  const timestamp = dayjs().format('DD-MMM-YYYY-hh-mm-ss-A');
  return `${fileName}-${timestamp}.xlsx`;
};

const buildExportSheetName = (sheetName) => {
  return sheetName;
};

const TimeSheetReport = () => {
  const { userProfile } = useAuth();

  // Default to list_view when permitted, otherwise fall back to calendar_view
  const getDefaultViewType = () => {
    const listPermissions = userProfile?.permissions?.[MODULE_NAMES.PROJECT_TIME_SHEET_REPORT_LIST_VIEW] || [];
    const calendarPermissions
      = userProfile?.permissions?.[MODULE_NAMES.PROJECT_TIME_SHEET_REPORT_CALENDAR_VIEW] || [];
    if (listPermissions.includes('view') || listPermissions.includes('read')) return 'list_view';
    if (calendarPermissions.includes('view') || calendarPermissions.includes('read')) return 'calendar_view';
    return 'list_view';
  };

  const [viewType, setViewType] = useState(getDefaultViewType);
  const [isAddEffortOpen, setIsAddEffortOpen] = useState(false);
  const [isEditEffortOpen, setIsEditEffortOpen] = useState(false);
  const [selectedEditRow, setSelectedEditRow] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', rowData: null });

  // Filter & Pagination States
  const [globalExternalFilters, setGlobalExternalFilters] = useState([]);
  const capturedGlobalFiltersRef = useRef([]);

  const paginationRef = useRef(INITIAL_PAGINATION);
  const [, forceUpdate] = useState(0);
  const triggerPaginationRender = useCallback(() => forceUpdate((n) => n + 1), []);

  const tableContainerRef = useRef(null);
  const tableStateRef = useRef(null);
  const domEnhancementRafRef = useRef(null);

  // Permissions
  const listViewPermissions
  = userProfile?.permissions?.[MODULE_NAMES.PROJECT_TIME_SHEET_REPORT_LIST_VIEW] || [];
  const calendarViewPermissions
  = userProfile?.permissions?.[MODULE_NAMES.PROJECT_TIME_SHEET_REPORT_CALENDAR_VIEW] || [];
  const userInfo = userProfile;
  // List View Permissions
  const canViewInListView = listViewPermissions.includes('view') || listViewPermissions.includes('read');
  const canAddInListView = listViewPermissions.includes('create') || listViewPermissions.includes('add');
  const canEditInListView = listViewPermissions.includes('update') || listViewPermissions.includes('edit');
  const canDeleteInListView = listViewPermissions.includes('delete') || listViewPermissions.includes('remove');

  // Calendar View Permissions
  const canViewInCalendarView = calendarViewPermissions.includes('view') || calendarViewPermissions.includes('read');
  const canAddInCalendarView = calendarViewPermissions.includes('create') || calendarViewPermissions.includes('add');

  // Permissions scoped to whichever view is currently active
  const canViewInCurrentView = viewType === 'list_view' ? canViewInListView : canViewInCalendarView;
  const canAddInCurrentView = viewType === 'list_view' ? canAddInListView : canAddInCalendarView;

  const baseURL = useMemo(
    () => buildServiceURL(serviceName.PROJECT_MS, `/api${TIME_SHEET_REPORT}/view/${viewType}`),
    [viewType],
  );

  // Normalizes filter formats explicitly
  // 1. Deduplicate filters by field in normalizeCapturedFilters
  const normalizeCapturedFilters = useCallback((filters) => {
    if (!Array.isArray(filters)) return [];

    const seenFields = new Map();

    filters.forEach((filter) => {
      if (!filter || typeof filter !== 'object' || typeof filter.field !== 'string') return;

      // Overwrite previous clones so only the latest applied filter per field remains
      seenFields.set(filter.field, filter);
    });

    return Array.from(seenFields.values()).map((filter) => {
      const minimalFilter = { field: filter.field };

      if (Array.isArray(filter.rangePlaceholders) && filter.rangePlaceholders.length > 0) {
        minimalFilter.rangePlaceholders = [...filter.rangePlaceholders];
      }
      if (
        Array.isArray(filter.range)
      && filter.range.some((v) => v !== null && v !== undefined && v !== '')
      ) {
        minimalFilter.range = [...filter.range];
      }
      if (
        Array.isArray(filter.selectedValues)
      && filter.selectedValues.length > 0
      ) {
        minimalFilter.selectedValues = [...filter.selectedValues];
      }
      if (
        filter.selectedValue !== null
      && filter.selectedValue !== undefined
      && filter.selectedValue !== ''
      ) {
        minimalFilter.selectedValue = filter.selectedValue;
      }
      if (
        filter.condition !== null
      && filter.condition !== undefined
      && filter.condition !== ''
      ) {
        minimalFilter.condition = filter.condition;
      }
      if (
        filter.value !== null
      && filter.value !== undefined
      && filter.value !== ''
      ) {
        minimalFilter.value = filter.value;
      }
      return minimalFilter;
    });
  }, []);

  // Compute what filters are allowed for the target view
  const buildViewScopedFilters = useCallback((filters, nextViewType) => {
    const availableFilters = TIME_SHEET_REPORT_AVAILABLE_FILTERS[nextViewType] || [];
    const normalizedFilters = normalizeCapturedFilters(filters);
    if (!availableFilters.length) return normalizedFilters;
    return normalizedFilters.filter((filter) => availableFilters.includes(filter.field));
  }, [normalizeCapturedFilters]);

  // 2. Synchronize globalExternalFilters immediately on Apply
  const handleFilterUpdate = useCallback(({ action, filters }) => {
    if (action === 'reset') {
      capturedGlobalFiltersRef.current = [];
      setGlobalExternalFilters([]);
      paginationRef.current = { ...INITIAL_PAGINATION };
      triggerPaginationRender();
      return;
    }

    if (action === 'apply') {
      const normalizedFilters = normalizeCapturedFilters(filters);
      const viewScopedFilters = buildViewScopedFilters(normalizedFilters, viewType);

      capturedGlobalFiltersRef.current = normalizedFilters;
      // Update globalExternalFilters immediately so it's ready before any view switch
      setGlobalExternalFilters(viewScopedFilters);
      paginationRef.current = { ...paginationRef.current, page: 1 };
      triggerPaginationRender();
    }
  }, [buildViewScopedFilters, normalizeCapturedFilters, triggerPaginationRender, viewType]);

  // View switch handling
  const handleViewChange = useCallback((newViewType) => {
    if (newViewType === viewType) return;

    const canViewTarget = newViewType === 'list_view' ? canViewInListView : canViewInCalendarView;
    if (!canViewTarget) {
      toast.error?.('You do not have permission to view this.');
      return;
    }

    // Inject previously captured view-scoped global filters before switching
    const nextExternalFilters = buildViewScopedFilters(
      capturedGlobalFiltersRef.current,
      newViewType,
    );

    setGlobalExternalFilters(nextExternalFilters);
    paginationRef.current = { ...INITIAL_PAGINATION };
    triggerPaginationRender();
    setViewType(newViewType);
  }, [buildViewScopedFilters, viewType, triggerPaginationRender, canViewInListView, canViewInCalendarView]);

  // Table refresh wrapper
  const triggerTableRefresh = useCallback(() => {
    const state = tableStateRef.current?.();
    state?.refreshTableData?.();
    state?.refetchData?.();
    state?.loadData?.();
  }, []);

  // API response handler
  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    const tableData = response?.data?.tableData ?? response?.data?.data?.tableData ?? [];
    const totalRow = response?.data?.totalRow ?? response?.data?.data?.totalRow;

    if (tableData.length && totalRow) {
      const merged = [...tableData, { ...totalRow, isTotalRow: true }];
      if (response?.data?.tableData) response.data.tableData = merged;
      else if (response?.data?.data?.tableData) response.data.data.tableData = merged;
    }

    const pg = response.data?.pagination
      ?? response.data?.paginationConfig
      ?? response.data?.data?.pagination
      ?? response.data?.data?.paginationConfig;

    const next = {
      page: pg?.page ?? pg?.currentPage ?? paginationRef.current.page,
      limit: pg?.limit ?? PAGE_SIZE,
      total: pg?.total ?? pg?.count ?? response.meta?.total ?? tableData.length ?? 0,
    };

    const prev = paginationRef.current;
    if (prev.page !== next.page || prev.limit !== next.limit || prev.total !== next.total) {
      paginationRef.current = next;
      triggerPaginationRender();
    } else {
      paginationRef.current = next;
    }
  }, [triggerPaginationRender]);

  const handlePaginationChange = useCallback((newPagination) => {
    paginationRef.current = newPagination;
    triggerPaginationRender();
  }, [triggerPaginationRender]);

  // Modals & Action Listeners
  const handleDeleteEffort = useCallback(async (rowData) => {
    const timeSheetId = resolveTimeSheetId(rowData || {});
    if (!timeSheetId) {
      toast.error?.('Unable to delete: missing timeSheetId.');
      return;
    }
    const response = await deleteLoggedEffort({
      authToken: userProfile?.access_token,
      employeeId: userProfile?.employeeId,
      timeSheetId,
      userInfo,
      tableViewType: viewType,
    });
    if (response?.status && response.status < 400) {
      toast.success?.(response?.data?.message ?? response?.message ?? 'Effort deleted successfully.');
      triggerTableRefresh();
      return;
    }
    throw new Error(response?.data?.message ?? response?.message ?? 'Failed to delete effort');
  }, [triggerTableRefresh, userProfile, userInfo, viewType]);

  const handleAddIconClick = useCallback(() => {
    if (!canAddInCurrentView) {
      toast.error?.('You do not have permission to add time sheet reports.');
      return;
    }
    setIsAddEffortOpen(true);
  }, [canAddInCurrentView]);

  const openConfirmDialog = useCallback((action, rowData) => setConfirmDialog({ open: true, action, rowData }), []);
  const closeConfirmDialog = useCallback(() => setConfirmDialog({ open: false, action: '', rowData: null }), []);

  const handleConfirmAction = useCallback(async () => {
    const { action, rowData } = confirmDialog;
    closeConfirmDialog();
    if (action === 'delete') {
      try {
        await handleDeleteEffort(rowData || {});
      } catch (err) {
        toast.error?.(err?.message || 'Failed to delete effort.');
      }
    }
  }, [closeConfirmDialog, confirmDialog, handleDeleteEffort]);

  const handleCellClick = useCallback((params) => {
    const { action, rowData } = params || {};
    if (rowData?.isTotalRow) return;
    if (action === 'edit') {
      if (!canEditInListView) {
        toast.error?.('You do not have permission to edit time sheet reports.'); return;
      }
      setSelectedEditRow(rowData || null);
      setIsEditEffortOpen(true);
      return;
    }
    if (action === 'delete') {
      if (!canDeleteInListView) {
        toast.error?.('You do not have permission to delete time sheet reports.'); return;
      }
      openConfirmDialog('delete', rowData);
    }
  }, [canDeleteInListView, canEditInListView, openConfirmDialog]);

  // DOM Enhancements
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    let observer = null;
    const blockExportClick = (e) => {
      e.stopImmediatePropagation();
      e.preventDefault();
    };

    const scheduleEnhancements = () => {
      if (domEnhancementRafRef.current) return;

      domEnhancementRafRef.current = window.requestAnimationFrame(() => {
        domEnhancementRafRef.current = null;
        if (observer) observer.disconnect();

        applyToolbarButtonClasses(container);
        applyTotalRowClass(container);

        // Lock button-group min-width to the widest it has been so far,
        // preventing layout shifts when the filter button loads in late.
        const buttonGroup = container.querySelector('.button-group');
        if (buttonGroup) {
          const currentWidth = buttonGroup.offsetWidth;
          const lockedWidth = parseFloat(buttonGroup.getAttribute('data-locked-w') || '0');
          if (currentWidth > lockedWidth) {
            buttonGroup.setAttribute('data-locked-w', String(currentWidth));
            buttonGroup.style.minWidth = `${currentWidth}px`;
          }
        }

        if (viewType === 'calendar_view') {
          container
            .querySelectorAll('.time-sheet-report-export-btn:not([data-export-blocked])')
            .forEach((btn) => {
              btn.setAttribute('data-export-blocked', 'true');
              btn.addEventListener('click', blockExportClick, true);
            });
        }

        if (observer) observer.observe(container, { childList: true, subtree: true });
      });
    };

    scheduleEnhancements();
    observer = new MutationObserver(() => scheduleEnhancements());
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      if (domEnhancementRafRef.current) {
        window.cancelAnimationFrame(domEnhancementRafRef.current);
        domEnhancementRafRef.current = null;
      }
      observer.disconnect();
    };
  }, [viewType]);

  const additionalElements = useMemo(() => [
    <React.Fragment key="view-toggle">
      <div className="view-button-wrapper">
        {canViewInListView && (
          <button
            className={`tsr-view-toggle-btn${viewType === 'list_view' ? ' active' : ''}`}
            onClick={() => handleViewChange('list_view')}
            title="List View"
            type="button"
          >
            <ListIcon />
          </button>
        )}
        {canViewInCalendarView && (
          <button
            className={`tsr-view-toggle-btn${viewType === 'calendar_view' ? ' active' : ''}`}
            onClick={() => handleViewChange('calendar_view')}
            title="Calendar View"
            type="button"
          >
            <CalendarIcon />
          </button>
        )}
      </div>
    </React.Fragment>,
  ], [viewType, handleViewChange, canViewInListView, canViewInCalendarView]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      headers: { token: userProfile?.access_token || '' },
      additionalPayloadData: { employeeId: userProfile?.employeeId || '' },
      userInfo: {
        id: userProfile?.id || '',
        emailId: userProfile?.email || '',
        permissions: userProfile?.permissions || [],
        roleDetails: userProfile?.roleDetails || [],
        employeeId: userProfile?.employeeId || '',
      },
      handlers: { handleAddIconClick },
      handler: {
        handleFilteredApiResponse,
        onFilterUpdate: handleFilterUpdate, // Directly handled via API here
        onCellClickHandler: handleCellClick,
        setTableStateHandler: (getState) => {
          tableStateRef.current = getState;
        },
      },
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => buildExportFileName('TimeSheetReport'),
          sheetName: () => buildExportSheetName('TimeSheetReport'),
        },
      },
      showToolBar: true,
      showPagination: true,
      toolBarSettings: {
        showAddNew: canAddInCurrentView,
        showFilterOption: true,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showDownloadExcel: true,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showToolBarHeading: true,
        toolBarHeading: 'Time Sheet Report',
        showReset: true,
        additionalElements,
      },
    },
    showFilterOption: true,
    tableSettings: {
      enableTreeTable: false,
      size: 'large',
      loaderSkeleton: { rows: 10, bodyCells: 1, columns: 8 },
      pageSize: PAGE_SIZE,
      paginationConfig: {
        ...paginationRef.current,
        onPaginationChange: handlePaginationChange,
        showPageSizeSelector: false,
      },
      showExpandCollapse: false,
      tableScrollHeight: '400px',
      maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
    },
  }), [
    baseURL,
    viewType,
    userProfile,
    additionalElements,
    handleCellClick,
    handleFilterUpdate,
    handleFilteredApiResponse,
    handlePaginationChange,
    handleAddIconClick,
    canAddInCurrentView,
  ]);

  return (
    <div className="time-sheet-report-outer">
      <div className="time-sheet-report-table">
        <div
          className={viewType === 'calendar_view' ? 'tsr-calendar-view' : 'time-sheet-report-table-utility'}
          ref={tableContainerRef}
        >
          {/* Note: The key is intentionally set to `viewType` to unmount and remount
          TableUtility safely and re-initialize global filters */}
          {canViewInCurrentView
            ? (
              <TableUtility
                key={viewType}
                configuration={configuration}
                externalFilters={globalExternalFilters}
              />
            )
            : <div className="time-sheet-report-no-permission">You do not have permission to view this.</div>}
        </div>

        { canAddInCurrentView && <AddEffortModal
          open={isAddEffortOpen}
          onClose={() => setIsAddEffortOpen(false)}
          onSubmit={triggerTableRefresh}
          authToken={userProfile?.access_token || ''}
          employeeId={userProfile?.employeeId || ''}
          userInfo={userInfo}
          tableViewType={viewType}
        />
        }

        {canEditInListView && <EditEffortModal
          open={isEditEffortOpen}
          rowData={selectedEditRow}
          onClose={() => {
            setIsEditEffortOpen(false);
            setSelectedEditRow(null);
          }}
          onSubmit={triggerTableRefresh}
          authToken={userProfile?.access_token || ''}
          employeeId={userProfile?.employeeId || ''}
          userInfo={userInfo}
          tableViewType={viewType}
        />
        }

        <ActionConfirmModal
          open={confirmDialog.open}
          currentAction="delete"
          title="Delete Effort"
          message="Do you want to delete this effort entry?"
          confirmText="Delete"
          cancelText="Cancel"
          onCancel={closeConfirmDialog}
          onConfirm={handleConfirmAction}
        />
      </div>
    </div>
  );
};

export default TimeSheetReport;
